// src/firestore/users/referrals/index.ts

import * as users from '..'
import * as path from '../../path'
import { firestore } from '../../../helpers/firebase/firestore'

/**
 * Path to the referral collection under a user.
 *
 * `users/{userId}/referral`
 */
export function collectionPath(userId: string): string {
  return path.append(users.documentPath(userId), 'referral')
}

/**
 * Path to a specific referral document.
 *
 * `users/{userId}/referral/{referralId}`
 */
export function documentPath(userId: string, referralId: string): string {
  return path.append(collectionPath(userId), referralId)
}

export type ReferralStatus = 'pending' | 'rewarded' | 'expired'

/**
 * Path to the referral history collection.
 *
 * `users/{userId}/referralHistory`
 */
export function historyCollectionPath(userId: string): string {
  return path.append(users.documentPath(userId), 'referralHistory')
}

export interface ReferralData {
  /** Unique referral code for this user */
  referralCode: string
  /** Total number of successful referrals */
  totalReferrals: number
  /** Total rewards earned from referrals */
  totalRewardsEarned: number
  /** Timestamp when referral code was generated */
  codeGeneratedAt: firestore.Timestamp
  /** Total CARD tokens earned from referrals */
  referralTokensEarned: number
  /** Current referral tier (bronze, silver, gold, etc) */
  currentTier: 'bronze' | 'silver' | 'gold' | 'diamond'
  /** Progress towards next tier */
  tierProgress: number
  /** Next tier threshold */
  nextTierAt: number
}

export interface ReferralHistory {
  /** ID of the referred user */
  referredUserId: string
  /** Status of this referral */
  status: ReferralStatus
  /** When this referral was created */
  createdAt: firestore.Timestamp
  /** When rewards were claimed (if applicable) */
  rewardedAt?: firestore.Timestamp
  /** Tokens earned from this referral */
  tokensEarned: number
}

export const REFERRAL_REWARDS = {
  initial: 100, // Tokens for referrer when new user joins
  signup: 50, // Tokens for referee when they sign up
  milestones: {
    5: 500, // 5 referrals bonus
    10: 1000, // 10 referrals bonus
    25: 3000, // 25 referrals bonus
    50: 7000, // 50 referrals bonus
  },
  tiers: {
    bronze: {
      multiplier: 1,
      threshold: 0,
    },
    silver: {
      multiplier: 1.2,
      threshold: 5,
    },
    gold: {
      multiplier: 1.5,
      threshold: 15,
    },
    diamond: {
      multiplier: 2,
      threshold: 30,
    },
  },
}
